import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, SearchIcon, CheckIcon } from 'lucide-react';
const HeroSection = () => {
  // Animation states for bullet points and underlines
  const [animationStates, setAnimationStates] = useState({
    bulletPoint1: false,
    bulletPoint2: false,
    bulletPoint3: false,
    underlineReframe: false,
    underlineInterpret: false,
    underlineCreate: false
  });
  // Trigger animations with staggered timing
  useEffect(() => {
    const animationTimers = [
    // Bullet point animations
    setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      bulletPoint1: true
    })), 1300), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      bulletPoint2: true
    })), 1600), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      bulletPoint3: true
    })), 1900),
    // Sequential underline animations (after bullet points appear)
    setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      underlineReframe: true
    })), 2200), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      underlineInterpret: true
    })), 2800), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      underlineCreate: true
    })), 3400)];
    // Cleanup timers on unmount
    return () => animationTimers.forEach(timer => clearTimeout(timer));
  }, []);
  return <section className="min-h-screen overflow-hidden bg-[#FFF5EB]" data-id="element-213">
      {/* Background with subtle pattern */}
      <div className="absolute inset-0 w-full h-full" data-id="element-214">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#E87B5C08_1px,transparent_1px),linear-gradient(to_bottom,#E87B5C08_1px,transparent_1px)] bg-[size:24px_24px]" data-id="element-215"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white to-[#FFF5EB]" data-id="element-216"></div>
      </div>
      {/* Content */}
      <div className="relative z-20 flex items-center min-h-screen" data-id="element-217">
        <div className="max-w-7xl mx-auto w-full px-4 py-12 md:py-16" data-id="element-218">
          <div className="flex flex-col md:flex-row items-start justify-between gap-12" data-id="element-219">
            {/* Left Column */}
            <div className="md:w-1/2 md:pt-0 md:-mt-20" data-id="element-220">
              {/* Logo */}
              <div className="mb-5" data-id="element-221">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mJeiiWYx1MRS4z54BWWeae/Final_Emigo_Logo_%28Transparent%29-Photoroom.png" alt="Emigo Logo" className="h-[300px] md:h-[375px] w-auto" data-id="element-222" />
              </div>
              {/* Content container - moved up more */}
              <div className="flex flex-col -mt-8" data-id="element-223">
                {/* Text content group - now static except for bullet points */}
                <div className="space-y-12 md:space-y-16 mb-8" data-id="element-224">
                  {/* Main Heading - static now */}
                  <h1 className="text-3xl md:text-6xl font-bold leading-tight text-[#E87B5C]" data-id="element-225">
                    <span className="block" data-id="element-226">Say what you mean,</span>
                    <span className="block whitespace-nowrap" data-id="element-227">
                      not what you'll regret.
                    </span>
                  </h1>
                  {/* Sub Heading - static now */}
                  <div data-id="element-228">
                    <h2 className="text-xl md:text-3xl font-bold text-gray-600 mb-4" data-id="element-229">
                      Emigo's got your back when you need to:
                    </h2>
                    {/* Branded checkmark list with individual animations - increased spacing between items */}
                    <div className="space-y-6 mb-8" data-id="element-230">
                      {/* Bullet point 1 - with italics and underline animation */}
                      <div className={`flex items-start transform transition-all duration-500 ease-out ${animationStates.bulletPoint1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-231">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E87B5C] flex items-center justify-center mr-3" data-id="element-232">
                          <CheckIcon className="h-4 w-4 text-white" data-id="element-233" />
                        </div>
                        <span className="text-lg md:text-2xl text-gray-600" data-id="element-234">
                          <span className={`relative italic ${animationStates.underlineReframe ? 'after:content-[""] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#E87B5C] after:w-full after:transition-all after:duration-500' : ''}`} data-id="element-235">
                            Reframe
                          </span>{' '}
                          a heated or unclear text
                        </span>
                      </div>
                      {/* Bullet point 2 - with italics and underline animation */}
                      <div className={`flex items-start transform transition-all duration-500 ease-out ${animationStates.bulletPoint2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-236">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E87B5C] flex items-center justify-center mr-3" data-id="element-237">
                          <CheckIcon className="h-4 w-4 text-white" data-id="element-238" />
                        </div>
                        <span className="text-lg md:text-2xl text-gray-600" data-id="element-239">
                          <span className={`relative italic ${animationStates.underlineInterpret ? 'after:content-[""] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#E87B5C] after:w-full after:transition-all after:duration-500' : ''}`} data-id="element-240">
                            Interpret
                          </span>{' '}
                          a tricky message and how to best reply
                        </span>
                      </div>
                      {/* Bullet point 3 - with italics and underline animation */}
                      <div className={`flex items-start transform transition-all duration-500 ease-out ${animationStates.bulletPoint3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-241">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E87B5C] flex items-center justify-center mr-3" data-id="element-242">
                          <CheckIcon className="h-4 w-4 text-white" data-id="element-243" />
                        </div>
                        <span className="text-lg md:text-2xl text-gray-600" data-id="element-244">
                          <span className={`relative italic ${animationStates.underlineCreate ? 'after:content-[""] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#E87B5C] after:w-full after:transition-all after:duration-500' : ''}`} data-id="element-245">
                            Create
                          </span>{' '}
                          the right words when you're stuck
                        </span>
                      </div>
                    </div>
                    {/* Closing line - updated to be slightly more grey and "real time" in italics */}
                    <p className="text-base md:text-xl text-gray-500" data-id="element-246">
                      All in <em data-id="element-247">real time</em>, without switching apps: whether
                      you're trying to land a date, keep your relationship
                      strong, stay calm with family, or sound professional at
                      work.
                    </p>
                  </div>
                </div>
                {/* Buttons - static now and reduced margin-top by 30% */}
                <div className="flex flex-col w-full mt-11 items-start" data-id="element-248">
                  {/* Changed from flex-col to flex-row and increased width */}
                  <div className="flex flex-col sm:flex-row gap-4 w-full max-w-[580px]" data-id="element-249">
                    <a href="#download" className="group px-6 py-3 bg-gradient-to-br from-[#E87B5C] to-[#D16A4A] text-white text-lg font-medium rounded-full hover:opacity-90 transition-all flex items-center justify-center min-w-[160px] shadow-sm hover:shadow-md" data-id="element-250">
                      Download Now
                      <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-251" />
                    </a>
                    <a href="#how-it-works" className="px-6 py-3 bg-white text-[#E87B5C] text-lg font-medium rounded-full border-2 border-[#E87B5C]/30 hover:border-[#E87B5C]/50 transition-all flex items-center justify-center min-w-[160px]" data-id="element-252">
                      Why Emigo
                      <SearchIcon className="ml-2 h-5 w-5" data-id="element-253" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {/* Right Column - iPhone - Updated for better centering on mobile */}
            <div className="w-full md:w-1/2 flex justify-center items-center mt-8 md:mt-0 md:items-start md:pt-24" data-id="element-254">
              <div className="relative mx-auto w-[240px] md:w-[290px] h-[520px] md:h-[630px] bg-black rounded-[45px] md:rounded-[55px] border-[4px] md:border-[5px] border-black p-2 transform rotate-3 transition-all duration-300 hover:rotate-0 hover:animate-wiggle" data-id="element-255">
                {/* Sound waves animation (visible on hover) */}
                <div className="absolute -left-12 top-1/2 -translate-y-1/2 opacity-0 hover:opacity-100 transition-opacity duration-300" data-id="element-256">
                  <div className="sound-wave sound-wave-1" data-id="element-257"></div>
                  <div className="sound-wave sound-wave-2" data-id="element-258"></div>
                  <div className="sound-wave sound-wave-3" data-id="element-259"></div>
                </div>
                <div className="absolute -right-12 top-1/2 -translate-y-1/2 opacity-0 hover:opacity-100 transition-opacity duration-300" data-id="element-260">
                  <div className="sound-wave sound-wave-1" data-id="element-261"></div>
                  <div className="sound-wave sound-wave-2" data-id="element-262"></div>
                  <div className="sound-wave sound-wave-3" data-id="element-263"></div>
                </div>
                {/* iPhone Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[100px] md:w-[120px] h-[20px] md:h-[25px] bg-black rounded-b-[20px] z-20" data-id="element-264"></div>
                {/* Content Area */}
                <div className="w-full h-full bg-[#f5f5f5] rounded-[35px] md:rounded-[40px] overflow-hidden" data-id="element-265">
                  <div className="flex items-center justify-center h-full text-[#E87B5C]" data-id="element-266">
                    Video Coming Soon
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default HeroSection;